<script lang="ts" setup>
  import { ref, computed } from 'vue';

  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';
  import {
    MpfAttachmentsProps,
    mpfAttachmentsDefaults,
    MpfAttachmentsEmits,
  } from './MpfAttachments.props';
  import './MpfAttactments.scss';
  import MpfFileUpload from '../MpfFileUpload/MpfFileUpload.vue';
  import MpfFilesSummary from '../MpfFilesSummary/MpfFilesSummary.vue';

  const props = withDefaults(defineProps<MpfAttachmentsProps>(), mpfAttachmentsDefaults);
  const emit = defineEmits<MpfAttachmentsEmits>();

  defineOptions({ inheritAttrs: false });

  const files = ref<File[]>([...(props.initialValue ?? []), ...(props.modelValue ?? [])]);
  const fileErrors = ref<string | null>(null);

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);

  const groupClasses = computed(() => [
    'mpf-form-group',
    hasErrors.value || fileErrors.value ? 'mpf-form-group--error' : '',
  ]);

  const maxSizeMb = props.maxFileSize / (1024 * 1024);

  const hintId = computed(() => (props.hint ? `${props.id}-hint` : undefined));
  const errorId = computed(() => (hasErrors.value ? `${props.id}-error` : undefined));

  function onFileChange(incoming: File[]) {
    fileErrors.value = null;
    const validFiles: File[] = [];
    for (const file of incoming) {
      if (file.size > props.maxFileSize) {
        fileErrors.value = `${file.name} exceeds the maximum file size of ${maxSizeMb}MB.`;
        continue;
      }
      validFiles.push(file);
    }

    const nextFiles = [...files.value, ...validFiles].slice(0, props.maxFiles);
    files.value = nextFiles;
    emit('update:modelValue', nextFiles);
  }

  function onFilesSummaryChange(nextFiles: File[]) {
    fileErrors.value = null;

    files.value = nextFiles;

    emit('update:modelValue', nextFiles);
  }

  function onFileRemove(payload: { file: File; index: number }) {
    console.log(
      'REMOVE fired',

      payload,

      files.value.map((f) => f.name),
    );

    fileErrors.value = null;
  }
</script>

<template>
  <div :class="groupClasses">
    <MpfLabel
      :label="label"
      :required="required"
      :heading-level="headingLevel"
      :heading-level-size="headingLevelSize"
    />

    <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

    <div class="mpf-u-margin-top-4 mpf-u-margin-bottom-4">
      <div class="mpf-message mpf-message--warning mpf-message--with-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          class="mpf-icon"
          focusable="false"
          aria-hidden="true"
        >
          <path
            d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24l0 112c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-112c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"
          ></path>
        </svg>
        <div class="mpf-u-margin-left-2">
          <h2 class="mpf-heading-s mpf-u-margin-bottom-1">Warning</h2>
          <p class="mpf-u-margin-bottom-0">
            Do not upload photos of sensitive or intimate body parts, such as the groin, genitals,
            anus, bottom or breasts.
          </p>
        </div>
      </div>
    </div>

    <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
      {{ errors![0] }}
    </div>

    <div v-if="fileErrors" class="mpf-error-message" role="alert" aria-live="polite">
      {{ fileErrors }}
    </div>
    <mpf-file-upload
      :id="`file-upload-${id}`"
      :hint-id="`file-hint-id-${id}`"
      :model-value="modelValue"
      :accept="accept"
      @change="onFileChange"
      @invalid="fileErrors = $event"
      :disabled="files.length >= maxFiles"
      :files-count="files.length"
      class="mpf-u-margin-bottom-6"
      :multiple="props.multiple"
    />
    <mpf-files-summary
      :model-value="files as unknown as File[]"
      @update:model-value="onFilesSummaryChange"
      @remove="onFileRemove"
      :allow-delete="true"
    />
  </div>
</template>
